import { CheckIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { assignInlineVars } from '@vanilla-extract/dynamic'
import type { CSSProperties } from 'react'
import { match } from 'ts-pattern'
import * as styles from './phase-stepper.css.ts'

export type PhaseStepperProps<TPhase extends string> = {
  /** Ordered phases to render as steps. */
  phases: readonly TPhase[]
  /** Human label for each phase. */
  labels: Record<TPhase, string>
  /** The phase currently in progress. */
  currentPhase: TPhase
  /**
   * Background for the current step's indicator. Lets each feature keep its own accent (threat model
   * uses drift, exploit chains uses pulse) while sharing this component and its layout.
   */
  currentColor?: string
  /**
   * Foreground for that indicator. Whoever chooses the background has to be able to choose a legible
   * foreground for it — the default white fails contrast on a light accent.
   */
  currentTextColor?: string
}

/**
 * Ordered progress stepper for a multi-phase generation. Phases before the current one render as
 * done (checkmark), the current one is highlighted, and later ones are pending. Shared by the
 * Threat Model and Exploit Chains tabs.
 */
export const PhaseStepper = <TPhase extends string>({
  phases,
  labels,
  currentPhase,
  currentColor,
  currentTextColor,
}: PhaseStepperProps<TPhase>) => {
  const currentIndex = phases.indexOf(currentPhase)
  // Both vars are always assigned, falling back to the shared defaults, so neither needs a conditional.
  const style = assignInlineVars({
    [styles.currentStepBg]: currentColor ?? mappedColors.primary.drift,
    [styles.currentStepFg]: currentTextColor ?? mappedColors.neutral.halo,
  }) as CSSProperties

  return (
    <ol className={styles.stepper} aria-label="Generation progress" style={style}>
      {phases.map((phase, index) => {
        const state = match(Math.sign(index - currentIndex))
          .with(-1, () => 'done' as const)
          .with(0, () => 'current' as const)
          .otherwise(() => 'pending' as const)
        return (
          <li key={phase} className={styles.step} aria-current={state === 'current' ? 'step' : undefined}>
            <span className={styles.stepIndicator({ state })} aria-hidden="true">
              {state === 'done' ? <CheckIcon size={12} weight="bold" /> : index + 1}
            </span>
            <span className={styles.stepLabel({ state })}>{labels[phase]}</span>
          </li>
        )
      })}
    </ol>
  )
}
